import React, { useEffect, useId, useState } from 'react';
import { Modal } from './Modal';
import { Channel, CHANNEL_NAME_MAX_LEN } from '../types';

interface RenameChannelModalProps {
  isOpen: boolean;
  channel: Channel;
  onClose: () => void;
  /**
   * 새 이름으로 저장. true를 돌려주면 부모가 모달을 닫는다(성공).
   * false면 모달을 그대로 두고 버튼을 되살린다 — 오류 토스트는 부모가 이미 띄웠다.
   */
  onSubmit: (name: string) => Promise<boolean>;
}

/**
 * 그룹 대화방 이름 변경 입력 모달. 대화방 목록·홈·모바일 헤더의 컨텍스트 메뉴에서 열린다.
 * 서버 성공 뒤에만 로컬 반영하므로(낙관적 갱신 없음) 저장 중에는 이중 제출을 막는다.
 */
export const RenameChannelModal: React.FC<RenameChannelModalProps> = ({
  isOpen,
  channel,
  onClose,
  onSubmit,
}) => {
  const fid = useId();
  // 자동 이름 방(이름을 정한 적 없음)은 빈 칸으로 연다 — 지금 이름은 '나를 뺀 멤버'라 사람마다 다르다.
  // 그 이름을 그대로 채우면 손대지 않고 '변경'을 눌렀을 때 내 화면의 이름이 모두에게 고정된다.
  const initialName = channel.nameAuto ? '' : (channel.name ?? '');
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);

  // 부모가 renameTarget && <Modal>로 조건부 렌더하므로 사실상 마운트마다 초기화되지만,
  // 같은 인스턴스로 다른 채널을 받는 경우를 대비해 방어적으로 다시 채운다
  useEffect(() => {
    setName(initialName);
    setSaving(false);
  }, [channel.id, initialName]);

  const trimmed = name.trim();
  // 자동 이름 방은 지금 보이는 이름과 같은 글자여도 '이름을 정하는' 변경이다
  const canSubmit = !saving && trimmed.length > 0 && (channel.nameAuto === true || trimmed !== (channel.name ?? ''));

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const ok = await onSubmit(trimmed);
    // 성공하면 부모가 언마운트하므로 setState를 부르지 않는다(언마운트 후 갱신 경고 방지)
    if (!ok) setSaving(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="대화방 이름 변경"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            취소
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-600"
          >
            {saving ? '변경 중…' : '변경'}
          </button>
        </div>
      }
    >
      <label htmlFor={`${fid}-name`} className="block text-sm font-medium text-slate-700 mb-2">
        대화방 이름
      </label>
      <input
        id={`${fid}-name`}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          // 한글 마지막 음절 확정 Enter가 제출로 새지 않도록 IME 조합 가드 (ChatWindow와 동일)
          if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.keyCode === 229) return;
          e.preventDefault();
          void submit();
        }}
        placeholder={channel.nameAuto ? (channel.name ?? undefined) : undefined}
        maxLength={CHANNEL_NAME_MAX_LEN}
        disabled={saving}
        className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400 transition-colors disabled:bg-slate-50"
      />
      <p className="mt-1.5 flex justify-between text-xs text-slate-500">
        <span>
          {channel.nameAuto
            ? '지금은 참여자 이름으로 보입니다. 정하면 모두에게 같은 이름으로 보입니다'
            : '참여자 모두에게 같은 이름으로 보입니다'}
        </span>
        <span className="tabular-nums">{trimmed.length}/{CHANNEL_NAME_MAX_LEN}</span>
      </p>
    </Modal>
  );
};
